/**
 * EL RESUMEN MENSUAL DEL SEGUIMIENTO, LISTO PARA PINTAR (19-oct-2026).
 *
 * Lee el documento que escribe el backend en `bots/{botId}/seguimientoAuto_resumen/{YYYY-MM}`
 * (`functions/bot/seguimientoAuto/resumenMensual.js`): `toques.enviados.{n}`, `respuestas.toque{n}`
 * —con `toque0` para quien respondió antes de cualquier toque— y `cierres.{motivo}`.
 *
 * ⚠️ LOS CONTADORES SON POR POSICIÓN, NO POR PELDAÑO (`barrido.js:213`, `:259`, `revisita.js:106`):
 * «toque 1» es el primero que se envió, sea el «👀» o el dato útil según `ordenToques`. Por eso la
 * pantalla rotula «por toque (posición)» y nunca «por peldaño»: con el orden cambiado a mitad de mes,
 * leerlo por peldaño mezclaría dos cosas distintas en la misma fila.
 *
 * PURA: sin Firestore ni React, para que la decisión tenga dónde ponerse roja.
 */

const N_TOQUES = [1, 2, 3];
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** La clave del mes del documento (`YYYY-MM`) en la zona del bot. */
export function mesDelResumen(fecha = new Date(), timeZone = 'America/Santiago') {
  const partes = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit' }).formatToParts(fecha);
  const y = partes.find((p) => p.type === 'year').value;
  const m = partes.find((p) => p.type === 'month').value;
  return `${y}-${m}`;
}

/** El mes anterior a una clave `YYYY-MM`. */
export function mesAnterior(clave) {
  const [y, m] = String(clave).split('-').map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}

/**
 * @param {object|null} data  el documento tal cual (o null si no existe)
 * @returns {{ vacio: boolean, porToque: Array<{n:number, enviados:number, respondidos:number, tasa:number|null}>, sinToque: number, cierres: Array<{motivo:string, n:number}>, tasa: number|null }}
 */
export function vistaDelResumen(data) {
  const d = data && typeof data === 'object' ? data : {};
  const env = (d.toques && d.toques.enviados) || {};
  const resp = d.respuestas || {};
  const porToque = N_TOQUES
    .map((n) => {
      const enviados = num(env[n]);
      const respondidos = num(resp[`toque${n}`]);
      return { n, enviados, respondidos, tasa: enviados > 0 ? respondidos / enviados : null };
    })
    .filter((f) => f.enviados > 0 || f.respondidos > 0);
  const sinToque = num(resp.toque0);
  const cierres = Object.entries(d.cierres || {})
    .map(([motivo, n]) => ({ motivo, n: num(n) }))
    .filter((c) => c.n > 0)
    .sort((a, b) => b.n - a.n || a.motivo.localeCompare(b.motivo));
  const totalEnviados = porToque.reduce((s, f) => s + f.enviados, 0);
  const totalRespondidos = porToque.reduce((s, f) => s + f.respondidos, 0);
  return {
    vacio: porToque.length === 0 && sinToque === 0 && cierres.length === 0,
    porToque,
    sinToque,
    cierres,
    tasa: totalEnviados > 0 ? totalRespondidos / totalEnviados : null,
  };
}
